import { useState } from "react";
import { EditEvidenceDialog } from "@/components/IntakeDialog";
import { EvidenceThumb } from "@/components/EvidenceThumb";
import { Search, ScanSearch, Loader2, Pencil, Image as ImageIcon, FileText } from "lucide-react";

function TriageBadge({ e }) {
  if (e.triage_status === "pending")
    return <span data-testid="triaging-badge" className="text-[9px] font-mono text-monk-blue flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> TRIAGING</span>;
  if (e.ai_drafted && e.triage_status === "confirmed")
    return <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm text-emerald-400 border border-emerald-500/30 bg-emerald-500/10">REVIEWED</span>;
  if (e.ai_drafted && (e.triage_status === "drafted" || e.triage_status === "error"))
    return <span data-testid="pending-review-badge" className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm text-monk-warn border border-monk-warn/40 bg-monk-warn/10">PENDING REVIEW</span>;
  return null;
}

function StatusCell({ e }) {
  const analyzing = ["queued", "running"].includes(e.orchestration_status);
  const findingsCount = Object.keys(e.engine_findings || {}).length;
  if (analyzing)
    return <span className="text-[9px] font-mono text-monk-blue flex items-center gap-1" data-testid="evidence-analyzing"><Loader2 className="w-3 h-3 animate-spin" /> ANALYZING</span>;
  if (findingsCount > 0)
    return <span className="text-[9px] font-mono text-emerald-400 flex items-center gap-1" data-testid="evidence-findings-count"><ScanSearch className="w-3 h-3" /> {findingsCount} FINDING{findingsCount === 1 ? "" : "S"}</span>;
  return <span className="text-[9px] font-mono text-neutral-600">—</span>;
}

export function EvidenceTable({ evidence, caseId, onOpen, onUpdated }) {
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const list = evidence || [];

  const filtered = list
    .map((e, i) => ({ e, i }))
    .filter(({ e }) => {
      if (!q.trim()) return true;
      const hay = `${e.label || ""} ${e.content || ""} ${(e.tags || []).join(" ")} ${e.notes || ""} ${e.kind || ""}`.toLowerCase();
      return hay.includes(q.toLowerCase());
    });

  return (
    <div className="bg-monk-panel border border-white/10 rounded-md overflow-hidden" data-testid="evidence-table">
      <div className="px-3 py-2.5 border-b border-white/10 flex items-center gap-2">
        <Search className="w-4 h-4 text-neutral-500 shrink-0" />
        <input
          data-testid="evidence-search"
          value={q}
          onChange={(ev) => setQ(ev.target.value)}
          placeholder="Search label, content, tags, notes…"
          className="bg-transparent text-sm text-white placeholder:text-neutral-600 focus:outline-none w-full"
        />
        <span className="font-mono text-[10px] text-neutral-500 shrink-0">{filtered.length}/{list.length}</span>
      </div>

      <div className="max-h-[560px] overflow-auto">
        {filtered.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-neutral-500 text-sm">{list.length === 0 ? "No evidence logged. Use Bulk Intake to ingest scans." : "No evidence matches your search."}</p>
          </div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead className="sticky top-0 bg-monk-panel z-10">
              <tr className="border-b border-white/10">
                <th className="overline px-3 py-2 w-10">#</th>
                <th className="overline px-3 py-2">Evidence Item</th>
                <th className="overline px-3 py-2 hidden md:table-cell w-28">Status</th>
                <th className="overline px-3 py-2 hidden lg:table-cell w-32">Triage</th>
                <th className="overline px-3 py-2 hidden xl:table-cell">Tags</th>
                <th className="overline px-3 py-2 w-16 text-right">Edit</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(({ e, i }) => (
                <tr key={e.id} data-testid="evidence-row" onClick={() => onOpen(e, i)}
                  className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors group">
                  <td className="px-3 py-2.5 font-mono text-[11px] text-monk-blue align-top">E{i + 1}</td>
                  <td className="px-3 py-2.5 align-top">
                    <div className="flex items-start gap-2.5">
                      {e.file_id ? (
                        <EvidenceThumb fileId={e.file_id} alt={e.label} className="w-9 h-9 rounded-sm object-cover border border-white/10 shrink-0" />
                      ) : (
                        <span className="w-9 h-9 rounded-sm bg-black/50 border border-white/10 flex items-center justify-center shrink-0">
                          <FileText className="w-4 h-4 text-neutral-600" />
                        </span>
                      )}
                      <div className="min-w-0">
                        <div className="hard-data text-[13px] font-medium text-neutral-100 truncate">{e.label}</div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="overline">{e.kind}</span>
                          {e.file_id && <ImageIcon className="w-3 h-3 text-neutral-600" />}
                        </div>
                        {e.content && <p className="text-[11px] text-neutral-500 line-clamp-1 mt-0.5 md:hidden">{e.content}</p>}
                        {e.notes && <p className="text-[11px] text-neutral-500 italic line-clamp-1 mt-0.5" data-testid="evidence-notes">{e.notes}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 align-top hidden md:table-cell"><StatusCell e={e} /></td>
                  <td className="px-3 py-2.5 align-top hidden lg:table-cell"><TriageBadge e={e} /></td>
                  <td className="px-3 py-2.5 align-top hidden xl:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {(e.tags || []).slice(0, 4).map((t, idx) => (
                        <span key={idx} className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-monk-blue/10 border border-monk-blue/30 text-monk-blue">{t}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 align-top text-right">
                    <button onClick={(ev) => { ev.stopPropagation(); setEdit(e); }} data-testid="edit-evidence-btn"
                      className="inline-flex items-center justify-center w-7 h-7 rounded-sm border border-white/10 text-neutral-500 hover:text-white hover:border-white/30 transition-colors">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <EditEvidenceDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} caseId={caseId} evidence={edit} onUpdated={onUpdated} />
    </div>
  );
}
